// ========================================
// COMPOSANT RichTextEditor
// Éditeur de texte riche avec upload Cloudinary
// ========================================

import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { uploadToCloudinary } from '../../../services/cloudinary/config';
import { FiLoader, FiCheck, FiAlertCircle } from 'react-icons/fi';
import './RichTextEditor.css';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Commencez à écrire...',
  minHeight = 200 
}) => {
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success' | 'error' | null

  // Reset du statut après affichage
  useEffect(() => {
    if (uploadStatus) {
      const timer = setTimeout(() => setUploadStatus(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [uploadStatus]);

  // Upload de l'image sélectionnée vers Cloudinary
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation du type
    if (!file.type.startsWith('image/')) {
      setUploadStatus('error');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus(null);

    try {
      // Upload vers Cloudinary
      const result = await uploadToCloudinary(file, 'portfolio/blog', (progress) => {
        setUploadProgress(progress);
      });

      // Insérer l'URL de l'image dans l'éditeur (pas le base64 !)
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', result.url);
        quill.setSelection(range.index + 1);
      }

      setUploadStatus('success');
    } catch (error) {
      console.error('Erreur upload image:', error);
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset le file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handler personnalisé pour intercepter le clic sur le bouton image
  const imageHandler = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Configuration des modules Quill
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), [imageHandler]);

  // Formats autorisés
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  return (
    <div className="rich-text-editor" style={{ '--min-height': `${minHeight}px` }}>
      {/* Input caché pour sélectionner les fichiers */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* Overlay de progression d'upload */}
      {isUploading && (
        <div className="editor-upload-overlay">
          <div className="editor-upload-modal">
            <div className="upload-spinner">
              <FiLoader className="spinner" />
            </div>
            <p className="upload-text">Upload de l'image...</p>
            <div className="editor-progress-bar">
              <div 
                className="editor-progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className="editor-progress-text">{uploadProgress}%</span>
          </div>
        </div>
      )}

      {/* Toast de succès/erreur */}
      {uploadStatus && (
        <div className={`editor-toast ${uploadStatus}`}>
          {uploadStatus === 'success' ? (
            <>
              <FiCheck />
              <span>Image ajoutée !</span>
            </>
          ) : (
            <>
              <FiAlertCircle />
              <span>Erreur lors de l'upload</span>
            </>
          )}
        </div>
      )}

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;